import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import FrontechLogo from './FrontechLogo';
import { BarChart3, MessageSquare, Clock, TrendingUp, Bot, ArrowUpRight } from 'lucide-react';

function MiniBar({ value, max, color }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div className="flex-1 rounded-full overflow-hidden" style={{ height: 6, background: 'var(--color-elevated)' }}>
      <div className="h-full rounded-full transition-all duration-700"
        style={{ width: `${pct}%`, background: color || 'var(--color-accent)' }} />
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, accent }) {
  return (
    <div className="rounded-2xl p-5 flex flex-col gap-3"
      style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)' }}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold tracking-widest uppercase" style={{ color: 'var(--color-text-muted)' }}>
          {label}
        </span>
        <div className="w-8 h-8 rounded-xl flex items-center justify-center"
          style={{ background: `${accent}20` }}>
          <Icon size={15} style={{ color: accent }} />
        </div>
      </div>
      <div>
        <p className="font-heading text-3xl font-bold tracking-tight stat-number"
          style={{ color: 'var(--color-text-primary)' }}>
          {value}
        </p>
        {sub && <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>{sub}</p>}
      </div>
    </div>
  );
}

export default function Analytics() {
  const { api } = useAuth();
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api().get('/api/agents');
        setAgents(data);
      } catch (err) { console.error(err); }
      setLoading(false);
    })();
  }, [api]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <FrontechLogo size={40} variant="loader" color="var(--color-accent)" />
      </div>
    );
  }

  const totals = agents.reduce(
    (acc, a) => ({
      conversations: acc.conversations + (a.total_conversations || 0),
      messages:      acc.messages      + (a.total_messages || 0),
      minutes:       acc.minutes       + (a.total_minutes || 0),
    }),
    { conversations: 0, messages: 0, minutes: 0 }
  );

  const maxMessages = Math.max(...agents.map(a => a.total_messages || 0), 1);
  const maxConvs    = Math.max(...agents.map(a => a.total_conversations || 0), 1);

  return (
    <div>
      <div className="mb-8">
        <h1 data-testid="analytics-heading"
          className="font-heading text-2xl font-bold tracking-tight"
          style={{ color: 'var(--color-text-primary)' }}>
          Analytics
        </h1>
        <p className="mt-1 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
          Performance overview across all your voice agents
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard icon={Bot}          label="Agents"        value={agents.length}               sub="active"    accent="#5B7FA6" />
        <StatCard icon={TrendingUp}   label="Conversations" value={totals.conversations}        sub="all time"  accent="#00D67E" />
        <StatCard icon={MessageSquare} label="Messages"     value={totals.messages}             sub="exchanged" accent="#8B5CF6" />
        <StatCard icon={Clock}        label="Minutes"       value={totals.minutes.toFixed(1)}   sub="voice time" accent="#F59E0B" />
      </div>

      {agents.length > 0 ? (
        <div className="rounded-2xl overflow-hidden"
          style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)' }}>
          <div className="px-6 py-4 border-b flex items-center justify-between"
            style={{ borderColor: 'var(--color-border)' }}>
            <h2 className="font-heading text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
              Agent Breakdown
            </h2>
            <BarChart3 size={15} style={{ color: 'var(--color-text-muted)' }} />
          </div>
          <div className="divide-y" style={{ borderColor: 'var(--color-border)' }}>
            {agents.map((agent, i) => {
              const color = agent.widget_color || '#00D67E';
              return (
                <div key={agent.id} data-testid={`analytics-card-${i}`}
                  className="px-6 py-5 flex items-center gap-5 transition-colors cursor-default"
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--color-elevated)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <div className="flex items-center gap-3 w-44 flex-shrink-0">
                    <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: color }} />
                    <span className="text-sm font-medium truncate" style={{ color: 'var(--color-text-primary)' }}>
                      {agent.name}
                    </span>
                  </div>
                  <div className="flex-1 grid grid-cols-3 gap-6">
                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>Messages</span>
                        <span className="text-xs font-semibold" style={{ color: 'var(--color-text-secondary)' }}>
                          {agent.total_messages || 0}
                        </span>
                      </div>
                      <MiniBar value={agent.total_messages || 0} max={maxMessages} color={color} />
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>Convos</span>
                        <span className="text-xs font-semibold" style={{ color: 'var(--color-text-secondary)' }}>
                          {agent.total_conversations || 0}
                        </span>
                      </div>
                      <MiniBar value={agent.total_conversations || 0} max={maxConvs} color={color} />
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>Minutes</span>
                        <p className="text-sm font-semibold mt-0.5" style={{ color: 'var(--color-text-secondary)' }}>
                          {(agent.total_minutes || 0).toFixed(1)}
                        </p>
                      </div>
                      <ArrowUpRight size={14} style={{ color: 'var(--color-text-muted)' }} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="rounded-2xl p-12 text-center"
          style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)' }}>
          <FrontechLogo size={48} variant="animated" color="var(--color-accent)" />
          <h3 className="font-heading text-base font-semibold mt-5 mb-2"
            style={{ color: 'var(--color-text-primary)' }}>
            No data yet
          </h3>
          <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
            Create an agent and embed it on your site to start seeing analytics here.
          </p>
        </div>
      )}
    </div>
  );
}
